import { isRecord } from "@arti/utilities";
import { useEffect, useState } from "react";

import { useDashboardAuth } from "./useDashboardAuth";

type TeamsChatSdkEnabledState =
  | { status: "loading"; enabled: false; isAdmin: false; botReady: false }
  | { status: "ready"; enabled: boolean; isAdmin: boolean; botReady: boolean };

/** The probe body carries `is_admin`, and both channel-scoped Teams mutations
 *  are org-admin only. Reading it here lets callers render the state without
 *  offering controls that would answer 403 -- previously the flag was fetched
 *  and discarded, so the only way to discover the restriction was to hit it. */
export function readTeamsAdminFlag(body: unknown): boolean {
  return readTeamsBooleanFlag(body, "is_admin");
}

/** `enabled` answers "does this org have the Teams chat SDK turned on", which is
 *  not the same question as "can this deployment reach Teams". A deployment can
 *  have the flag on and no active bot registration, in which case bind/unbind
 *  would fail at the first call -- so surfaces offering those actions gate on
 *  botReady instead. */
export function readTeamsBotReadyFlag(body: unknown): boolean {
  return readTeamsBooleanFlag(body, "bot_ready");
}

function readTeamsBooleanFlag(body: unknown, key: string): boolean {
  return isRecord(body) && key in body && body[key] === true;
}

export function useTeamsChatSdkEnabled(
  probePath = "/user/teams/status",
): TeamsChatSdkEnabledState {
  const { organizationId } = useDashboardAuth();
  const [state, setState] = useState<TeamsChatSdkEnabledState>({
    botReady: false,
    enabled: false,
    isAdmin: false,
    status: "loading",
  });

  useEffect(() => {
    let cancelled = false;
    const load = async () => {
      const response = await fetch(
        buildTeamsChatSdkProbePath(probePath, organizationId),
        { credentials: "include" },
      );
      const body: unknown = response.ok
        ? await response.json().catch(() => null)
        : null;
      if (!cancelled) {
        setState({
          botReady: readTeamsBotReadyFlag(body),
          enabled: response.ok,
          isAdmin: readTeamsAdminFlag(body),
          status: "ready",
        });
      }
    };
    void load().catch(() => {
      if (!cancelled) {
        setState({
          botReady: false,
          enabled: false,
          isAdmin: false,
          status: "ready",
        });
      }
    });
    return () => {
      cancelled = true;
    };
  }, [organizationId, probePath]);

  return state;
}

export function buildTeamsChatSdkProbePath(
  probePath: string,
  organizationId: string | null,
): string {
  if (!organizationId) {
    return probePath;
  }
  const url = new URL(probePath, "http://localhost");
  url.searchParams.set("organization_id", organizationId);
  return `${url.pathname}${url.search}${url.hash}`;
}
